import React, { ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';
import { ModalContainer, ModalContent, ModalFooter, ModalHeader } from 'components/@common/Modal';
import PageContainer from 'components/@layout/PageContainer';
import { Formik, FormikValues, setIn } from 'formik';
import UPDATE_STUDENT from 'graph/student/update-student-mutation';
import GET_STUDENT_AND_CLASSROOM from 'graph/composite/get-student-and-classroom-query';
import useParseActionRouteParams from 'hooks/route-action/use-parse-action-route-params';
import { Typography } from '@material-ui/core';
import { Group, License, SourceKind, Student } from 'types/schema-generated-types';
import maybeGetExternalId from 'utilities/external-id-util';
import { filterMatManGroups } from 'utilities/group-utils';
import { getFullName } from 'utilities/name-utils';
import * as Yup from 'yup';
import {
  secretCodeTest,
  usernameTest
} from '../../../../Classrooms/modals/utils/student-form-validation';
import StudentDetailsForm from '../../../components/StudentDetailsForm';

const getValidationProps = (source?: SourceKind, usernames?: Array<string>): object => {
  const emailMessage = 'Must be an valid email';
  const requiredMessage = 'Field is required';
  return {
    validate: (values: FormikValues): object => {
      let studentErrors = {};
      const studentSchema = Yup.object().shape({
        firstName: Yup.string().required(requiredMessage),
        lastName: Yup.string().required(requiredMessage),
        username: usernameTest.test('Duplicate', 'Duplicate username', function(this, fieldValue) {
          const found =
            usernames &&
            usernames.find(
              (username: string) =>
                username.trim().toLowerCase() === fieldValue.trim().toLowerCase()
            );
          return !found;
        }),
        email:
          source === SourceKind.GOOGLE
            ? Yup.string()
                .email(emailMessage)
                .required(requiredMessage)
            : Yup.string()
                .email(emailMessage)
                .nullable(),
        gradeLevel: Yup.string().required(requiredMessage),
        secretCode: secretCodeTest
      });

      try {
        studentSchema.validateSync(values, {
          stripUnknown: false,
          strict: true,
          abortEarly: false
        });
      } catch (validationError) {
        validationError?.inner?.forEach((error: { path: string; message: string }) => {
          studentErrors = {
            ...studentErrors,
            ...setIn(studentErrors, error.path, error.message)
          };
        });
      }
      return studentErrors;
    },
    validateOnChange: true,
    validateOnBlur: true
  };
};

interface EditStudentModalFormProps {
  onSubmitSuccess: () => void;
  onClose: Function;
}

const EditStudentModalForm: React.FC<EditStudentModalFormProps> = ({
  onClose,
  onSubmitSuccess
}) => {
  const { t } = useTranslation('EditStudentModalForm');
  const { studentId, classroomId } = useParseActionRouteParams();
  const { data, loading } = useQuery(GET_STUDENT_AND_CLASSROOM, {
    variables: { whereStudent: { id: +studentId }, whereClassroom: { id: +classroomId } },
    skip: !studentId || !classroomId
  });
  const [updateStudent] = useMutation(UPDATE_STUDENT);

  const closeModal = useCallback((): void => {
    onClose();
  }, [onClose]);

  const handleCancelClick = useCallback((): void => {
    closeModal();
    return;
  }, [closeModal]);

  const mapStudentClassroomGroup = (group: Group): Group[] =>
    [
      {
        ...group,
        classroomId: Number(classroomId),
        groupCategory: group?.groupCategory && {
          ...group.groupCategory,
          classroomId: Number(classroomId)
        }
      },
      ...data?.student.groups?.filter((group: Group) => group.classroomId !== Number(classroomId))
    ].map(group => ({
      ...group,
      groupCategory: { ...group.groupCategory, __typename: undefined },
      __typename: undefined
    }));

  const validation = React.useMemo(
    () =>
      getValidationProps(
        data?.student.externalId?.sourceKind ||
          data?.classroom.externalId?.sourceKind ||
          data?.classroom.organization?.externalId?.sourceKind,
        data?.classroom.students
          .filter(({ id }: Student) => {
            return id !== data?.student.id;
          })
          .map(({ username }: Student) => username)
      ),
    [data]
  );
  if (loading) {
    return null;
  }

  return (
    <Formik
      enableReinitialize
      initialValues={{
        firstName: data?.student.firstName,
        lastName: data?.student.lastName,
        username: data?.student.username,
        email: data?.student.email,
        gradeLevel: data?.student.gradeLevel,
        secretCode: { code: data?.student.password, loginType: data?.student.loginType },
        group: filterMatManGroups(data?.student.groups)?.find(
          (group: Group) => group.classroomId === Number(classroomId)
        ),
        licenses: data?.student.licenseUsage?.map((license: License) => ({
          productCode: license.product?.code
        }))
      }}
      onSubmit={async (values): Promise<void> => {
        const { secretCode, group, ...restValues } = values;
        const res = await updateStudent({
          variables: {
            data: {
              ...restValues,
              loginType: secretCode.loginType,
              password: secretCode.code,
              groups: group && mapStudentClassroomGroup(group),
              licenses: undefined,
              ...maybeGetExternalId({
                candidateSourceId: restValues.email,
                entities: [data.classroom.organization]
              })
            },
            where: { id: +studentId }
          }
        });
        if (res) {
          onSubmitSuccess();
        }
      }}
      {...validation}
    >
      {({ dirty, isSubmitting, isValid, submitForm }): ReactElement => (
        <PageContainer>
          <ModalContainer
            Header={
              <ModalHeader>
                <Typography variant="h2" component="h1">
                  {t('Edit Student Details')} -{' '}
                  <b>{data?.student ? getFullName(data.student) : ''}</b>
                </Typography>
              </ModalHeader>
            }
            Footer={
              <ModalFooter
                onCancel={handleCancelClick}
                onSubmit={submitForm}
                submitDisabled={!dirty || !isValid || isSubmitting}
              />
            }
          >
            <ModalContent>
              <StudentDetailsForm isEdit />
            </ModalContent>
          </ModalContainer>
        </PageContainer>
      )}
    </Formik>
  );
};

export default EditStudentModalForm;
